// HTTP client for the DocFlow API (same origin, /api/...). Keeps the login token in localStorage.
import type { User } from './types';

const TOKEN_KEY = 'docflow.token';
const USER_KEY = 'docflow.user';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) { super(message); this.status = status; }
}

let onUnauthorized: (() => void) | null = null;
export function setUnauthorizedHandler(fn: () => void) { onUnauthorized = fn; }

export function getToken(): string | null { return localStorage.getItem(TOKEN_KEY); }
export function getStoredUser(): User | null { try { const s = localStorage.getItem(USER_KEY); return s ? JSON.parse(s) : null; } catch { return null; } }
export function storeSession(token: string, user: User) { localStorage.setItem(TOKEN_KEY, token); localStorage.setItem(USER_KEY, JSON.stringify(user)); }
export function clearSession() { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); }

async function request<T>(method: string, path: string, body?: any, raw?: { contentType?: string; headers?: Record<string, string> }): Promise<T> {
  const headers: Record<string, string> = { ...(raw?.headers || {}) };
  const token = getToken();
  if (token) headers['Authorization'] = 'Bearer ' + token;
  let payload: any = undefined;
  if (body !== undefined && body !== null) {
    if (raw) { headers['Content-Type'] = raw.contentType || 'application/octet-stream'; payload = body; }
    else { headers['Content-Type'] = 'application/json'; payload = JSON.stringify(body); }
  }
  let res: Response;
  try { res = await fetch(path, { method, headers, body: payload }); }
  catch (e) { throw new ApiError(0, 'Serverul nu răspunde. Verificați conexiunea.'); }
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    if (res.status === 401 && onUnauthorized && !path.endsWith('/login')) onUnauthorized();
    const msg = data && typeof data === 'object' && data.error ? data.error : (typeof data === 'string' && data ? data : `Eroare ${res.status}`);
    throw new ApiError(res.status, msg);
  }
  return data as T;
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: any) => request<T>('POST', path, body),
  put: <T>(path: string, body?: any) => request<T>('PUT', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  postText: <T>(path: string, text: string, contentType = 'text/plain') => request<T>('POST', path, text, { contentType }),
  upload: <T>(file: File) => request<T>('POST', '/api/files', file, { contentType: file.type || 'application/octet-stream', headers: { 'X-File-Name': encodeURIComponent(file.name) } }),
  /** Sends a file's raw bytes to any endpoint (e.g. Excel imports). */
  uploadTo: <T>(path: string, file: File) => request<T>('POST', path, file, { contentType: file.type || 'application/octet-stream', headers: { 'X-File-Name': encodeURIComponent(file.name) } }),
  /** GET URL with the token as a query parameter (for plain download links). */
  authUrl: (path: string) => path + (path.includes('?') ? '&' : '?') + 'token=' + encodeURIComponent(getToken() || ''),
  /** POSTs JSON and saves the binary answer as a file (e.g. generated Excel). */
  downloadPost: async (path: string, body: any, fileName: string) => {
    const res = await fetch(path, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + (getToken() || '') }, body: JSON.stringify(body) });
    if (!res.ok) throw new ApiError(res.status, `Eroare ${res.status}`);
    const blob = await res.blob();
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = fileName; document.body.appendChild(a); a.click();
    setTimeout(() => { URL.revokeObjectURL(a.href); a.remove(); }, 1000);
  },
  /** URL usable in <iframe>/<img> — the token travels as a query parameter. */
  fileUrl: (fileId: string, kind: 'preview' | 'download' | 'raw' = 'preview') => {
    const t = encodeURIComponent(getToken() || '');
    if (kind === 'preview') return `/api/files/${fileId}/preview?token=${t}`;
    if (kind === 'download') return `/api/files/${fileId}?download=true&token=${t}`;
    return `/api/files/${fileId}?token=${t}`;
  },
};

export function qs(params: Record<string, any>): string {
  const p = Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== '').map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`);
  return p.length ? '?' + p.join('&') : '';
}
